import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import get from 'lodash/get';
import { Form, Control, Fieldset, actions } from 'react-redux-form';
import MaskedInput from 'react-text-mask';
import BaseModal from '../BaseModal';
import Button from '../Button';
import {
  baseIndicateInterestPath,
  enumStates,
  PLACEHOLDER_OPT,
} from '../Forms/Schemas/baseRrf';
import {
  indicateInterest,
  cancelIndication,
} from '../../actions/indicateInterest';
import {
  validateName,
  validateEmail,
  validatePhone,
} from '../Forms/Validators';
import Select from '../Forms/Controls/Select';
import InputField from '../Forms/Controls/Field';
import blueCheck from '../../assets/images/blue_confirmation_check.png';
import noticeImage from '../../assets/images/notice.svg';
import CloseIcon from '../../assets/images/close_gray.svg';
import ComingSoonOfferDisclaimer from '../ComingSoonOfferDisclaimer';
import './OfferIndicateInterest.css';

const DUPLICATE_EMAIL_INDICATION_OF_INTEREST_MESSAGE = 'This email has already indicated interest for this offer.';

const personalInformationFields = [
  {
    model: '.firstName',
    RRFComp: Control.text,
    control: {
      placeholder: 'First Name',
      maxLength: 20,
      errors: {
        required: val => !val || !val.length,
        name: val => val && !validateName(val),
      },
    },
    error: {
      messages: {
        required: 'Please provide your First Name',
        name: 'First Name may only include letters, spaces, dashes and periods.',
      },
    },
  },
  {
    model: '.lastName',
    RRFComp: Control.text,
    control: {
      maxLength: 35,
      placeholder: 'Last Name',
      errors: {
        required: val => !val || !val.length,
        name: val => val && !validateName(val),
      },
    },
    error: {
      messages: {
        required: 'Please provide your Last Name.',
        name: 'Last Name may only include letters, spaces, dashes, and periods.',
      },
    },
  },
  {
    model: '.email',
    RRFComp: Control.text,
    control: {
      placeholder: 'Email',
      type: 'email',
      errors: {
        required: val => !val || !val.length,
        isEmail: val => val && !validateEmail(val),
      },
    },
    error: {
      messages: {
        required: 'Please provide your email address.',
        isEmail: val => `${val} is not a valid Email Address.`,
      },
    },
  },
  {
    model: '.phone',
    RRFComp: Control,
    inputType: 'text',
    control: {
      component: MaskedInput,
      placeholder: 'Phone Number',
      parser: val => (val && val.replace(/\s|\(|\)|-/g, '')),
      mask: [' ', '(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
      maxLength: 15,
      guide: false,
      style: {
        width: '100%',
        border: 'none',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: '#706e6e',
      },
      errors: {
        required: val => !val || !val.length,
        isPhone: val => val && !validatePhone(val),
      },
    },
    error: {
      messages: {
        required: 'A daytime phone number is required.',
        isPhone: val => `${val} is not a valid Telephone Number.`,
      },
    },
  },
  {
    model: '.state',
    RRFComp: Control,
    control: {
      component: Select,
      errors: {
        required: val => !val || !val.length || val === PLACEHOLDER_OPT,
      },
    },
    error: {
      messages: {
        required: 'Please select a State.',
      },
    },
  },
  {
    model: '.investmentRange',
    RRFComp: Control.radio,
  },
];

class OfferIndicateInterest extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showIndicateInterestDialog: false,
      showIndicationActionDialog: false,
      showAlreadySubmittedIndicationDialog: false,
      showCancelIndicationDialog: false,
      selectedRange: '',
      userHasIndicatedInterest: this.props.offering.OfferWatches &&
        this.props.offering.OfferWatches.length > 0 &&
        this.props.offering.OfferWatches.filter(o => o.deletedAt === null || o.deletedAt.length === 0).length > 0,
      hover: false,
    };
  }

  onInvestmentRangeClick(ev) {
    this.setState({
      selectedRange: ev.target.value,
    });
  }

  handleIndicateInterestDialog() {
    this.setState({ showIndicateInterestDialog: !this.state.showIndicateInterestDialog });
  }

  handleIndicationActionDialog() {
    this.setState({ showIndicationActionDialog: !this.state.showIndicationActionDialog });
  }

  handleAlreadySubmittedIndicationDialog() {
    this.setState({ showAlreadySubmittedIndicationDialog: !this.state.showAlreadySubmittedIndicationDialog });
  }

  handleCancelIndicationDialog() {
    this.setState({ showCancelIndicationDialog: !this.state.showCancelIndicationDialog });
  }

  async cancelIndication({ id }) {
    await this.props.cancelIndication(id);
    this.setState({
      userHasIndicatedInterest: false,
      showIndicationActionDialog: true,
      showCancelIndicationDialog: false,
      selectedRange: '',
    });
  }

  async submitInterest({ id }) {
    try {
      await this.props.indicateInterest(id);
      this.setState({
        userHasIndicatedInterest: true,
        showIndicationActionDialog: true,
        showIndicateInterestDialog: false,
        selectedRange: '',
      });
      this.props.resetIndicateInterest();
    } catch (err) {
      this.props.resetIndicateInterest();
      const { message } = (await err.res.json()).error;
      if (message === DUPLICATE_EMAIL_INDICATION_OF_INTEREST_MESSAGE) {
        this.setState({
          showAlreadySubmittedIndicationDialog: true,
          showIndicateInterestDialog: false,
          selectedRange: '',
        });
      } else {
        throw err;
      }
    }
  }

  render() {
    const {
      user,
      offering,
      indicateInterestForm,
      styleSuff,
    } = this.props;
    const { hover, userHasIndicatedInterest } = this.state;
    const { investmentRange } = offering;
    const investmentRangeValues = investmentRange ? investmentRange.split(';') : [];

    const indicateInterestFormModel = `${baseIndicateInterestPath}.indicateInterest`;
    const linkToOffer = (<Link to={`/offers/${offering.id}`}>{offering.name}</Link>);
    let actionButton;
    if (!userHasIndicatedInterest || (userHasIndicatedInterest && !user)) {
      actionButton = (
        <div styleName={`investBtn${styleSuff}`}>
          <Button onClick={() => this.handleIndicateInterestDialog()}>Indicate Interest</Button>
        </div>
      );
    } else {
      actionButton = (
        <div styleName={`investBtn${styleSuff}`}>
          <Button
            style={{
              color: hover ? '#EE624D' : '#62A814',
              background: '#fff',
              border: `1px solid ${hover ? '#EE624D' : '#62A814'}`,
              'border-radius': '40px',
            }}
            onClick={() => this.handleCancelIndicationDialog()}
            onMouseOver={() => this.setState({ hover: true })}
            onMouseOut={() => this.setState({ hover: false })}
            onBlur={() => {}}
            onFocus={() => {}}
          >
            { hover ? 'Cancel Indication' : 'Indication Submitted'}
          </Button>
        </div>
      );
    }
    return (
      <div>
        {actionButton}
        { this.state.showIndicateInterestDialog ? (
          <BaseModal onClose={() => this.handleIndicateInterestDialog()}>
            <div styleName="dialogHeader">
              <button onClick={() => this.handleIndicateInterestDialog()} type="button" styleName="dialogHeaderCloseBtn">
                <img width="12" src={CloseIcon} alt="icon" />
              </button>
            </div>
            <div styleName="dialogTitle">
              Indication of Interest
            </div>
            <div styleName="dialogText">
              Please complete to indicate your desired investment amount. You will receive updates and notification when the Offer will be available for actual investment.
            </div>
            <ComingSoonOfferDisclaimer centered />
            <div styleName="dialogText">
              <Form
                model={indicateInterestFormModel}
              >
                <Fieldset model={indicateInterestFormModel}>
                  { !user ? personalInformationFields.map((field, idx) => (idx < 4 ? (<InputField key={field.model} fieldProps={field} />) : null)) : null}
                  { !user ? (
                    <InputField key={personalInformationFields[4].model} fieldProps={personalInformationFields[4]}>
                      {Object.keys(enumStates).map(key => (
                        <option key={key} value={key}>{enumStates[key]}</option>
                      ))}
                    </InputField>
                  ) : null }
                  <span styleName="investmentRangeLabel">Investment Range</span>
                  {investmentRangeValues.map((range, idx) => (
                    <div
                      styleName="investmentRangeOption"
                      style={{
                        color: this.state.selectedRange === range ? '#fff' : '#4D88BE',
                        'background-color': this.state.selectedRange === range ? '#0255A3' : 'transparent',
                      }}
                    >
                      <Control.radio
                        {...personalInformationFields[5].control}
                        key={personalInformationFields[5].model}
                        model={personalInformationFields[5].model}
                        id={`investmentRange${idx}`}
                        value={range}
                        styleName={`investmentRangeRadio${this.state.selectedRange === range ? 'Active' : ''}`}
                        onChange={e => this.onInvestmentRangeClick(e)}
                        style={{
                          'margin-right': '10px',
                          '-webkit-appearance': 'none',
                          '-moz-appearance': 'none',
                          display: 'inline-block',
                          'background-color': 'transparent',
                          border: 'none',
                          outline: 'none',
                          width: '20px',
                          height: '20px',
                          top: '4px',
                          position: 'relative',
                        }}
                      />
                      <label styleName="rangeLabel" htmlFor={`investmentRange${idx}`}>{range}</label>
                    </div>
                  ))}
                </Fieldset>
              </Form>
            </div>
            <div styleName="dialogActions">
              <Button
                onClick={() => this.submitInterest(offering)}
                disabled={indicateInterestForm.indicateInterest.investmentRange.length === 0 && offering.investmentRange}
              >
                Submit
              </Button>
              <Button stylename="btnSecondaryAlt" onClick={() => this.handleIndicateInterestDialog()}>
                Cancel
              </Button>
            </div>
          </BaseModal>
        ) : null }
        {this.state.showIndicationActionDialog ? (
          <BaseModal onClose={() => this.handleIndicationActionDialog()}>
            <div styleName="dialogHeader">
              <button onClick={() => this.handleIndicationActionDialog()} type="button" styleName="dialogHeaderCloseBtn">
                <img width="12" src={CloseIcon} alt="icon" />
              </button>
            </div>
            <div styleName="dialogTitle">
              <img src={blueCheck} alt="Warning" style={{ width: 120, height: 120 }} />
              <p>Success</p>
            </div>
            <div styleName="dialogText">
              {userHasIndicatedInterest ? `You will receive updates and notification when the Offer ${offering.name} will be available` : '' }
            </div>
            <div styleName="dialogActions">
              <Button stylename="btnSecondaryAlt" onClick={() => this.handleIndicationActionDialog()}>
                Close
              </Button>
            </div>
          </BaseModal>
        ) : null}
        {this.state.showCancelIndicationDialog ? (
          <BaseModal onClose={() => this.handleCancelIndicationDialog()}>
            <div styleName="dialogHeader">
              <button onClick={() => this.handleCancelIndicationDialog()} type="button" styleName="dialogHeaderCloseBtn">
                <img width="12" src={CloseIcon} alt="icon" />
              </button>
            </div>
            <div styleName="dialogTitle">
              Cancel Indication
            </div>
            <div styleName="dialogText">
              Are you sure you want to stop receiving updates and notifications for {linkToOffer}
            </div>
            <div styleName="dialogActions">
              <Button
                onClick={() => this.cancelIndication(offering)}
              >
                Cancel Indication
              </Button>
              <Button stylename="btnSecondaryAlt" onClick={() => this.handleCancelIndicationDialog()}>
                Do not cancel
              </Button>
            </div>
          </BaseModal>
        ) : null}
        {this.state.showAlreadySubmittedIndicationDialog ? (
          <BaseModal onClose={() => this.handleAlreadySubmittedIndicationDialog()}>
            <div styleName="dialogHeader">
              <button onClick={() => this.handleAlreadySubmittedIndicationDialog()} type="button" styleName="dialogHeaderCloseBtn">
                <img width="12" src={CloseIcon} alt="icon" />
              </button>
            </div>
            <div styleName="dialogTitle">
              <img src={noticeImage} alt="Warning" style={{ width: 120, height: 120 }} />
              <p>Indication Submitted</p>
            </div>
            <div styleName="dialogText">
              You already submitted an indication of interest. You will receive updates and notification when the Offer {linkToOffer} will be available for actual investment.
            </div>
            <div styleName="dialogActions">
              <Button stylename="btnSecondaryAlt" onClick={() => this.handleAlreadySubmittedIndicationDialog()}>
                Close
              </Button>
            </div>
          </BaseModal>
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  formData: get(state, `${baseIndicateInterestPath}.indicateInterest`),
  user: get(state, 'user.data'),
  indicateInterestForm: get(state, 'indicateInterestForm'),
});

const mapDispatchToProps = dispatch => ({
  indicateInterest: (...args) => dispatch(indicateInterest(...args)),
  cancelIndication: (...args) => dispatch(cancelIndication(...args)),
  resetIndicateInterest: () => dispatch(actions.reset(`${baseIndicateInterestPath}.indicateInterest`)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OfferIndicateInterest);



// WEBPACK FOOTER //
// ./src/components/Offering/OfferIndicateInterest.js